import { createFeatureSelector, createSelector } from '@ngrx/store';

import * as UserProfileFeatureState from './user-profile.state';
import { isOutsideUsPlace } from '@cpd-app/shared-module/constants/outside-us-place.const';

// RETRIEVE USER PROFILE SLICE OF STATE
export const selectUserProfileState =
  createFeatureSelector<UserProfileFeatureState.UserProfileState>(
    UserProfileFeatureState.userProfileFeatureKey
  );

// HAS USER PROFILE BEEN SAVED
export const selectHasUserProfileBeenSaved = createSelector(
  selectUserProfileState,
  (state) => state.isUserProfileSaved
);

// USER PROFILE
export const selectUserProfile = createSelector(
  selectUserProfileState,
  (state) => state.userProfile
);

export const selectUserShareProfile = createSelector(
  selectUserProfileState,
  (state) => state.userShareProfile
);

// SOME SURVEYS COMPLETE
export const selectIsAnySurveyComplete = createSelector(
  selectUserProfile,
  (up) =>
    up &&
    (up.isSkillsSurveyComplete ||
      up.isInterestsSurveyComplete ||
      up.isWorkValuesSurveyComplete ||
      up.isLifestyleSurveyComplete)
);

export const selectIsAllSurveyComplete = createSelector(
  selectUserProfile,
  (up) =>
    up &&
    up.isSkillsSurveyComplete &&
    up.isInterestsSurveyComplete &&
    up.isWorkValuesSurveyComplete &&
    up.isLifestyleSurveyComplete
);

// HAS THE USER UPLOADED A JST
export const selectIsJstUploaded = createSelector(
  selectUserProfile,
  (up) => up?.isJstUploaded ?? false
);

// MILITARY BRANCH CODE
export const selectMilitaryBranchCode = createSelector(
  selectUserProfile,
  (up) => (up?.militaryBranch ? up.militaryBranch?.branchCode : undefined)
);

export const selectIsAirForce = createSelector(
  selectMilitaryBranchCode,
  (branchCode) => branchCode === 'F'
);

export const selectIsArmy = createSelector(
  selectMilitaryBranchCode,
  (branchCode) => branchCode === 'A'
);

export const selectIsSpaceForce = createSelector(
  selectMilitaryBranchCode,
  (branchCode) => branchCode === 'H'
);

// HAS PRE GOAL
export const selectHasPreGoal = createSelector(
  selectUserProfile,
  (up) => up && up.userHasPreGoal
);
export const selectUserPreGoal = createSelector(
  selectUserProfile,
  (up) => up && up.userPreGoalOccupation
);

// PRIVACY POLICY ACCEPTED
export const selectPrivacyPolicyAccepted = createSelector(
  selectUserProfile,
  (up) => up && up.privacyPolicyAccepted
);

export const selectProfileWorkType = createSelector(
  selectUserProfile,
  (up) => up && up.profileWorkType
);

export const selectProfileWorkTypeSet = createSelector(
  selectUserProfile,
  (up) => up && Boolean(up.profileWorkType?.type)
);

/**
 * CPD-654. "Is there a real member behind this store yet", which is a
 * different question from "has that member picked a work type".
 *
 * The favourites load used `selectProfileWorkTypeSet` as its stand-in for
 * this. Work type is set by the Get Started landing choice and is unrelated
 * to favourites, so a member who never made that choice -- reachable with a
 * finished skills survey and three goals -- never loaded their favourites at
 * all. See `favorite-occupations.effects.ts`.
 */
export const selectUserIdSet = createSelector(selectUserProfile, (up) =>
  Boolean(up?.userId)
);

export const selectHighestUserEducation = createSelector(
  selectUserProfile,
  (up) =>
    up &&
    (up.userEducations || []).reduce((prev, current) => {
      if (prev) {
        return current.educationLevelId > prev.educationLevelId
          ? current
          : prev;
      }

      return current;
    }, null)
);

export const selectCurrentUserLocation = createSelector(
  selectUserProfile,
  // #37: the first U.S. place; "Outside the U.S." has no city, state or ZIP
  (up) => {
    const all = up?.userPreferredLocations || [];
    return up && all[all.findIndex((location) => !isOutsideUsPlace(location))];
  }
);
